import { createContext, useContext, useMemo } from "react";

import {
  useQuery as _useQuery,
  useMutation as _useMutation,
  useLazyQuery as _useLazyQuery,
  ApolloClient,
  ApolloError,
  QueryHookOptions,
  OperationVariables,
  DocumentNode,
  TypedDocumentNode,
  QueryResult,
  MutationHookOptions,
  MutationTuple,
  LazyQueryHookOptions,
  QueryTuple,
} from "@apollo/client";

import { doNothing } from "@kazoohr/helpers";
import {
  useTranslation,
  TFunction,
  TOptions,
} from "@kazoohr/internationalization";

import { getHookDefaultErrorHandler } from "./getHookDefaultErrorHandler";

export const ApiClientContext = createContext<{
  client: ApolloClient<object> | undefined;
  defaultOnSuccess: (data: any) => void;
  defaultOnError: (error: any) => void;
}>({
  client: undefined,
  defaultOnSuccess: doNothing,
  defaultOnError: doNothing,
});

/**
 * Execute a query against the federated API.
 */
export function useApiQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: QueryOptions<TData, TVariables> | undefined = {}
): QueryResult<TData, TVariables> {
  const onError = useErrorHandler(options);
  const { client } = useContext(ApiClientContext);
  return _useQuery(document, {
    errorPolicy: "none",
    fetchPolicy: "cache-first",
    ...options,
    client,
    onError,
  });
}

function useErrorHandler(options: {
  onError?: (error: ApolloError) => void;
  defaultToastErrorMessageOrTranslationKey?: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  const { defaultOnError } = useContext(ApiClientContext);
  const { t } = useTranslation();
  return useMemo(() => {
    if (options.onError) {
      return options.onError;
    }
    return getHookDefaultErrorHandler(defaultOnError, t, options);
  }, [options.onError, options.defaultToastErrorMessageOrTranslationKey]);
}

export type QueryOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> =
  | QueryHookOptions<TData, TVariables> & {
      defaultToastErrorMessageOrTranslationKey?: string;
    };

/**
 * Execute a mutation against the federated API.
 */
export function useApiMutation<TData = any, TVariables = OperationVariables>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: MutationOptions<TData, TVariables> | undefined = {}
): MutationTuple<TData, TVariables> {
  const { client } = useContext(ApiClientContext);
  const onError = useErrorHandler(options);
  const onCompleted = useSuccessHandler(options);
  return _useMutation(document, {
    errorPolicy: "none",
    ...options,
    client,
    onError,
    onCompleted,
  });
}

function getDefaultSuccessHandler({
  toastFn,
  t,
  defaultToastSuccessMessageOrTranslationKey,
  defaultToastSuccessTranslationOptions,
}: {
  toastFn: (message: any) => void;
  t: TFunction;
  defaultToastSuccessMessageOrTranslationKey: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  return () => {
    toastFn(
      t(
        defaultToastSuccessMessageOrTranslationKey,
        defaultToastSuccessTranslationOptions
      )
    );
  };
}

function useSuccessHandler(options: {
  onCompleted?: (data: any) => void;
  defaultToastSuccessMessageOrTranslationKey?: string;
  defaultToastSuccessTranslationOptions?: TOptions;
}) {
  const { defaultOnSuccess } = useContext(ApiClientContext);
  const { t } = useTranslation();
  return useMemo(() => {
    const {
      defaultToastSuccessMessageOrTranslationKey,
      defaultToastSuccessTranslationOptions,
    } = options;

    if (!defaultToastSuccessMessageOrTranslationKey) {
      return options.onCompleted;
    }

    const successHandler = getDefaultSuccessHandler({
      toastFn: defaultOnSuccess,
      t,
      defaultToastSuccessMessageOrTranslationKey,
      defaultToastSuccessTranslationOptions,
    });

    return (data: any) => {
      options.onCompleted?.(data);
      successHandler();
    };
  }, [options.onCompleted, options.defaultToastSuccessMessageOrTranslationKey]);
}

export type MutationOptions<TData = any, TVariables = OperationVariables> =
  | MutationHookOptions<TData, TVariables> & {
      defaultToastSuccessMessageOrTranslationKey?: string;
      defaultToastSuccessTranslationOptions?: TOptions;
      defaultToastErrorMessageOrTranslationKey?: string;
      defaultToastErrorTranslationOptions?: TOptions;
    };

/**
 * Execute a query against the federated API.
 */
export function useLazyApiQuery<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
>(
  document: DocumentNode | TypedDocumentNode<TData, TVariables>,
  options: LazyHookOptions<TData, TVariables> | undefined = {}
): QueryTuple<TData, TVariables> {
  const { client } = useContext(ApiClientContext);
  const onError = useErrorHandler(options);

  return _useLazyQuery(document, {
    errorPolicy: "none",
    ...options,
    client,
    onError,
  });
}

export type LazyHookOptions<
  TData = any,
  TVariables extends OperationVariables = OperationVariables
> =
  | LazyQueryHookOptions<TData, TVariables> & {
      defaultToastErrorMessageOrTranslationKey?: string;
    };
